// 列尾的「⋯」溢位選單 —— 破壞性動作的收納處。
//
// 2026-08-06 使用者:「所有的刪除功能應該都是藏在編輯裡面」+「收進『⋯』選單」。
//
// 慣例（2026-07-26 使用者原話，見 LedgerEntityCard.tsx 的註解）：
//   「停用 跟刪除 都放在編輯裡面」—— 破壞性動作不放在每一列都看得到的操作列，
//   容易誤觸。
//
// 但有些東西**根本沒有編輯表單**（附件、員工文件、勞健保投保異動、股份類別、
// 分配…）—— 為了一顆刪除硬做一張編輯表單並不合理，因為那些資料本來就沒有可編輯
// 的欄位。 這類就收進這個 ⋯ 選單：一樣不直接暴露在列上，一樣要兩步才刪得掉。
//
// 版面/行為抄自 PolicyInspector 原本的 StepActions（本檔即由它一般化而來），
// 所以全站的 ⋯ 選單長得一樣、行為一樣。

import { type ReactNode, useState } from 'react';

export interface RowMenuItem {
  label: ReactNode;
  onClick: () => void;
  /** 破壞性 → 紅字 + 紅底 hover。 */
  danger?: boolean;
  disabled?: boolean;
}

export function RowMenu({
  items,
  label,
  align = 'right',
  width = 'w-32',
}: {
  items: RowMenuItem[];
  /** 這一列代表什麼 —— 只用在 aria-label（「『2025 H1』的更多動作」）。 */
  label: string;
  align?: 'left' | 'right';
  width?: string;
}) {
  const [open, setOpen] = useState(false);
  const usable = items.filter(Boolean);
  if (usable.length === 0) return null;
  const itemCls = 'block w-full text-left px-3 py-1.5 text-xs disabled:opacity-40';

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-1.5 py-0.5 rounded text-slate-400 hover:text-slate-800 hover:bg-slate-100"
        aria-label={`「${label}」的更多動作`}
        aria-expanded={open}
      >
        ⋯
      </button>
      {open && (
        <>
          {/* 點外面關掉 —— 不用 document listener，一層透明遮罩就夠 */}
          <button
            type="button"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
            aria-label="關閉選單"
          />
          <div
            className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1 z-20 ${width} bg-white border border-slate-200 rounded shadow-lg py-1`}
          >
            {usable.map((it, i) => (
              <button
                key={i}
                type="button"
                disabled={it.disabled}
                className={
                  it.danger
                    ? `${itemCls} text-red-700 hover:bg-red-50`
                    : `${itemCls} text-slate-700 hover:bg-slate-50`
                }
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
